import { z } from 'zod';

/**
 * Draadformaat v1 van `/sync/*` - letterlijk uit
 * `smartkeur-api/docs/02-api-contract.md` §Synchronisatie, "Draadformaat v1 —
 * bindend voor API én app". Wijk hier niet van af zonder dat document mee te
 * nemen.
 *
 * Inkomend verkeer wordt met zod geparsed (valideren aan de netwerkrand);
 * uitgaand verkeer is getypeerd en wordt door de outbox gebouwd.
 *
 * Er gaat **geen stringcodering** over de draad: een antwoord binnen een
 * multi-instantie draagt een `multi_scope`-lijst `{container_node_id, index}`
 * van buiten naar binnen. De vertaling naar de sleutels van de opname-engine
 * staat op één plek: `features/survey/model/wire-keys.ts`.
 */

export const wireScopeEntrySchema = z.object({
    container_node_id: z.number().int(),
    index: z.number().int().min(0),
});

export type WireScopeEntry = z.infer<typeof wireScopeEntrySchema>;

/** Van buiten naar binnen, 0-based. Leeg buiten elke multi-container en bij `ask_once`. */
export type WireScope = readonly WireScopeEntry[];

const wireScopeSchema = z
    .array(wireScopeEntrySchema)
    .nullable()
    .default([])
    .transform((scope): WireScopeEntry[] => scope ?? []);

/** `"1450.00"` of `null`. Een getal van de server wordt netjes naar twee decimalen gezet. */
const amountSchema = z
    .union([z.string(), z.number()])
    .nullable()
    .default(null)
    .transform((value) => {
        if (value === null || value === '') {
            return null;
        }

        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed.toFixed(2) : null;
    });

/** `decimal` kolommen komen soms als string ("12.000") binnen. */
const numberishSchema = z
    .union([z.number(), z.string()])
    .nullable()
    .default(null)
    .transform((value) => {
        if (value === null || value === '') {
            return null;
        }

        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed : null;
    });

const idSchema = z.union([z.string(), z.number()]).transform(String);
const nullableString = z.string().nullable().default(null);
const nullableInt = z.number().int().nullable().default(null);

export const wireAnswerSchema = z.object({
    answer_uuid: z.string(),
    /** Alleen in `/sync/pull`: bij welke opname dit antwoord hoort. */
    inspection_uuid: z.string().optional(),
    question_id: z.number().int(),
    multi_scope: wireScopeSchema,
    option_ids: z.array(z.number().int()).nullable().default(null),
    value_number: numberishSchema,
    value_text: nullableString,
    report_text: nullableString,
    /** Waar het gebrek zit, in een handvol woorden. */
    location: nullableString,
    direct_cost: amountSchema,
    term_cost: amountSchema,
    improvement_cost: amountSchema,
    cost_group_id: nullableInt,
    urgency: nullableString,
    nhg_classification: nullableString,
    client_updated_at: nullableString,
});

export type WireAnswer = z.infer<typeof wireAnswerSchema>;

export const wireMultiCountSchema = z.object({
    inspection_uuid: z.string().optional(),
    container_node_id: z.number().int(),
    multi_scope: wireScopeSchema,
    count: z.number().int().min(0),
    client_updated_at: nullableString,
});

export type WireMultiCount = z.infer<typeof wireMultiCountSchema>;

export const wireFindingSchema = z.object({
    finding_uuid: z.string(),
    inspection_uuid: z.string().optional(),
    /**
     * Het onderdeel van een handmatige bevinding: de templateknoop plus de
     * instanties eromheen, zoals bij een antwoord. Een instantie zelf ("Berging
     * 2") is de container met zijn eigen regel als laatste. `null` en `[]`: geen
     * onderdeel (een afgeleide bevinding hoort bij haar antwoord).
     */
    node_id: nullableInt,
    multi_scope: wireScopeSchema,
    title: nullableString,
    description: nullableString,
    /** Waar het gebrek zit, in een handvol woorden. */
    location: nullableString,
    severity: nullableString,
    urgency: nullableString,
    direct_cost: amountSchema,
    term_cost: amountSchema,
    improvement_cost: amountSchema,
    cost_group_id: nullableInt,
    nhg_classification: nullableString,
    source: z.string().default('manual'),
    client_updated_at: nullableString,
});

export type WireFinding = z.infer<typeof wireFindingSchema>;

export const wirePhotoSchema = z.object({
    /** De mediaresource zelf: wat `DELETE /media/{uuid}` neemt, ook voor een foto van een ander apparaat. */
    uuid: nullableString,
    client_uuid: z.string(),
    inspection_uuid: z.string().optional(),
    /** Precies één van beide: de foto hoort bij een antwoord of bij een handmatige bevinding. */
    answer_uuid: nullableString,
    finding_uuid: nullableString,
    caption: nullableString,
    taken_at: nullableString,
    urls: z
        .object({ thumb: z.string().nullable().optional(), web: z.string().nullable().optional() })
        .nullable()
        .default(null),
});

export type WirePhoto = z.infer<typeof wirePhotoSchema>;

export const wireInspectionSchema = z.object({
    uuid: z.string(),
    order_uuid: nullableString,
    status: z.string(),
    /** `null` voor een dienst waarvoor geen vragenlijst is gepubliceerd; daar valt niets op te nemen. */
    template_version_uuid: nullableString,
    started_at: nullableString,
    completed_at: nullableString,
    notes: nullableString,
    answers: z.array(wireAnswerSchema).default([]),
    multi_counts: z.array(wireMultiCountSchema).default([]),
    findings: z.array(wireFindingSchema).default([]),
    photos: z.array(wirePhotoSchema).default([]),
});

export type WireInspection = z.infer<typeof wireInspectionSchema>;

export const wireOrderSchema = z.object({
    uuid: z.string(),
    number: idSchema,
    status: z.string(),
    scheduled_at: nullableString,
    scheduled_end_at: nullableString,
    service: z.object({ code: z.string(), label: z.string() }).nullable().default(null),
    address: z
        .object({
            street: z.string().nullable().default(null),
            house_number: idSchema.nullable().default(null),
            house_number_suffix: nullableString,
            postcode: nullableString,
            city: nullableString,
            /** Het bouwjaar uit de BAG; `null` als het object niet aan de BAG gekoppeld is. */
            bag_build_year: z.number().int().nullable().optional(),
        })
        .nullable()
        .default(null),
    client: z
        .object({
            name: nullableString,
            phone: nullableString,
            email: nullableString,
        })
        .nullable()
        .default(null),
    inspection_uuid: nullableString,
    /**
     * Aan wie de opdracht is toegewezen. Een apparaat van kantoor krijgt alle
     * opdrachten van het bedrijf; hieraan ziet Mijn dag welke van jou zijn.
     * Ontbreekt bij een server van vóór dit veld.
     */
    assigned_user_uuid: z.string().nullable().optional(),
});

export type WireOrder = z.infer<typeof wireOrderSchema>;

export const wireTemplateVersionSchema = z.object({
    uuid: z.string(),
    version: z.number().int(),
    checksum: z.string(),
    package_url: z.string(),
});

export type WireTemplateVersion = z.infer<typeof wireTemplateVersionSchema>;

export const bootstrapSchema = z.object({
    data: z.object({
        cursor: idSchema,
        server_time: nullableString,
        tenant: z
            .object({
                uuid: z.string(),
                name: z.string(),
                slug: nullableString,
                logo_url: nullableString,
                accent_color: nullableString,
            })
            .nullable()
            .default(null),
        user: z
            .object({ uuid: z.string(), name: z.string(), email: z.string() })
            .nullable()
            .default(null),
        roles: z.array(z.string()).default([]),
        permissions: z.array(z.string()).default([]),
        template_versions: z.array(wireTemplateVersionSchema).default([]),
        orders: z.array(wireOrderSchema).default([]),
        inspections: z.array(wireInspectionSchema).default([]),
    }),
});

export type BootstrapData = z.infer<typeof bootstrapSchema>['data'];

export const WIRE_CHANGE_ACTIONS = ['upsert', 'delete'] as const;

export const pullSchema = z.object({
    data: z.object({
        changes: z
            .array(
                z.object({
                    entity: z.string(),
                    action: z.enum(WIRE_CHANGE_ACTIONS),
                    uuid: z.string().nullable().default(null),
                    data: z.unknown().optional(),
                }),
            )
            .default([]),
        cursor: idSchema,
        has_more: z.boolean().default(false),
    }),
});

export type PullData = z.infer<typeof pullSchema>['data'];
export type PullChange = PullData['changes'][number];

export const problemSchema = z
    .object({
        type: z.string().default('about:blank'),
        title: z.string().default(''),
        status: z.number().int().optional(),
        detail: z.string().optional(),
    })
    .passthrough();

export type WireProblem = z.infer<typeof problemSchema>;

/**
 * Wat de inspecteur moet horen over een bewerking die wél is opgeslagen, maar niet
 * precies waar hij haar zette: gemaakt op een oudere versie van de vragenlijst die de
 * server intussen verving. `detail` is een Nederlandse zin voor op het scherm.
 */
export const noticeSchema = z
    .object({
        type: z.string(),
        title: z.string().default(''),
        detail: z.string(),
    })
    .passthrough();

export type WireNotice = z.infer<typeof noticeSchema>;

export const PUSH_RESULTS = ['applied', 'unchanged', 'conflict', 'rejected'] as const;

export type PushResult = (typeof PUSH_RESULTS)[number];

export const pushResponseSchema = z.object({
    data: z.object({
        batch_uuid: z.string(),
        results: z.array(
            z.object({
                op_uuid: z.string(),
                result: z.enum(PUSH_RESULTS),
                server_state: z.unknown().optional(),
                problem: problemSchema.nullable().default(null),
                notice: noticeSchema.nullable().default(null),
            }),
        ),
        recalculated: z.array(z.string()).default([]),
        cursor: idSchema.nullable().default(null),
    }),
});

export type PushResponse = z.infer<typeof pushResponseSchema>['data'];

export const mediaResponseSchema = z.object({
    data: z
        .object({
            /** De mediaresource noemt zijn uuid `id`. */
            id: z.string().optional(),
            uuid: z.string().optional(),
            client_uuid: z.string().optional(),
            /* Per beschikbare variant; zolang de varianten er niet zijn alleen `original`. */
            urls: z
                .object({
                    thumb: z.string().nullable().optional(),
                    web: z.string().nullable().optional(),
                    original: z.string().nullable().optional(),
                })
                .partial()
                .nullable()
                .optional(),
        })
        .passthrough(),
    meta: z.object({ result: z.string().optional() }).passthrough().optional(),
});

/* ------------------------------------------------------------------ */
/* Uitgaand: de vijf entiteiten van `POST /sync/push`.                 */
/* ------------------------------------------------------------------ */

export const WIRE_ENTITIES = [
    'inspection',
    'inspection_answer',
    'multi_count',
    'finding',
    'order',
] as const;

export type WireEntity = (typeof WIRE_ENTITIES)[number];

export type WireAction = (typeof WIRE_CHANGE_ACTIONS)[number];

export interface InspectionUpsertPayload {
    inspection_uuid: string;
    status: 'in_progress' | 'completed';
    started_at: string | null;
    completed_at: string | null;
    notes: string | null;
}

export interface AnswerUpsertPayload {
    inspection_uuid: string;
    answer_uuid: string;
    question_id: number;
    multi_scope: WireScopeEntry[];
    option_ids: number[] | null;
    value_number: number | null;
    value_text: string | null;
    report_text: string | null;
    location: string | null;
    direct_cost: string | null;
    term_cost: string | null;
    improvement_cost: string | null;
    cost_group_id: number | null;
    urgency: string | null;
    nhg_classification: string | null;
}

export interface AnswerDeletePayload {
    inspection_uuid: string;
    answer_uuid: string;
}

export interface MultiCountUpsertPayload {
    inspection_uuid: string;
    container_node_id: number;
    multi_scope: WireScopeEntry[];
    count: number;
}

export interface FindingUpsertPayload {
    inspection_uuid: string;
    finding_uuid: string;
    node_id: number | null;
    multi_scope: WireScopeEntry[];
    title: string | null;
    description: string | null;
    location: string | null;
    severity: string | null;
    urgency: string | null;
    direct_cost: string | null;
    term_cost: string | null;
    improvement_cost: string | null;
    cost_group_id: number | null;
    nhg_classification: string | null;
    source: 'manual';
}

export interface FindingDeletePayload {
    inspection_uuid: string;
    finding_uuid: string;
}

export interface OrderUpsertPayload {
    order_uuid: string;
    status: string;
    notes: string | null;
}

/** Eén bewerking zoals hij in de outbox staat: entiteit, actie en de payload die erbij hoort. */
export type WireOperationBody =
    | { entity: 'inspection'; action: 'upsert'; payload: InspectionUpsertPayload }
    | { entity: 'inspection_answer'; action: 'upsert'; payload: AnswerUpsertPayload }
    | { entity: 'inspection_answer'; action: 'delete'; payload: AnswerDeletePayload }
    | { entity: 'multi_count'; action: 'upsert'; payload: MultiCountUpsertPayload }
    | { entity: 'finding'; action: 'upsert'; payload: FindingUpsertPayload }
    | { entity: 'finding'; action: 'delete'; payload: FindingDeletePayload }
    | { entity: 'order'; action: 'upsert'; payload: OrderUpsertPayload };

export type WireOperation = WireOperationBody & {
    op_uuid: string;
    client_updated_at: string;
};

export interface PushRequest {
    batch_uuid: string;
    device_uuid: string;
    operations: WireOperation[];
}

/** Maximaal aantal bewerkingen per `POST /sync/push`. */
export const MAX_BATCH_SIZE = 200;
